import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from 'src/components/modals/SaveSearchModal/SaveSearchModal.module.scss';
import classNames from 'classnames';
import { useQueries } from '@tanstack/react-query';
import Icon from 'src/components/ui/Icon/Icon';
import Image from 'src/components/ui/Image/Image';
import { getGroupCountries } from 'src/services/countries';
import Modal from '../../layouts/Modal/Modal';

export type CountriesListModalProps = {
  isOpen: boolean;
  onClose: () => void;
  items: any[];
};

const CountriesListModal: React.FC<CountriesListModalProps> = ({ isOpen, onClose, items }) => {
  const { t, i18n } = useTranslation();
  const countryQueries = useQueries({
    queries: items.map(item => ({
      queryKey: ['countries', item.id, i18n.language],
      queryFn: () => getGroupCountries(item.id),
      enabled: isOpen,
    })),
  });

  const allCountries = useMemo(() => {
    const seen = new Set<number>();
    return countryQueries
      .flatMap(query => query.data ?? [])
      .filter(country => country && !seen.has(country.id) && seen.add(country.id));
  }, [countryQueries]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={classNames(styles.root, '!min-w-[320px] w-[320px] md:h-[70vh]')}>
        <div className={classNames(styles.head, '!mb-0')}>
          <span className="py-6 flex justify-center">{t('carrier_analytics.connected_countries')}</span>
        </div>
        <div className={classNames(styles.content, '!py-0 !px-[24px] h-[50vh]')}>
          <div className="flex flex-col w-full items-start">
            {allCountries.map(item => (
              <div
                key={item.id}
                className="flex w-full items-center gap-[12px] p-[12px] border-subtle-bottom last:border-none">
                <div className="flex justify-center items-center w-[28px] h-[28px] rounded-full">
                  <Image
                    imageUrl={item.flag_url || ''}
                    alt={`Flag of ${item.name}`}
                    className="rounded-full h-full w-auto"
                    fallbackComponent={<Icon name="package" width={20} height={20} />}
                  />
                </div>
                <span className="typo-text-14--medium">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CountriesListModal;
